/**
 * Find Account Hook
 * 아이디/비밀번호 찾기 관련 로직 관리
 */
import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { findAccount } from '../api/findAccountApi';

export type FindAccountType = 'id' | 'password';

export type FindAccountResult = {
    success: boolean;
    userId?: string;
    message?: string;
    exists?: boolean;
};

export const useFindAccount = () => {
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const navigate = useNavigate();

    const handleFindId = useCallback(async (name: string, email: string): Promise<FindAccountResult> => {
        setIsLoading(true);
        setError(null);

        // 유효성 검사
        if (!name || !name.trim()) {
            setError('이름을 입력해주세요.');
            setIsLoading(false);
            return {
                success: false,
                message: '이름을 입력해주세요.',
            };
        }

        if (!email || !email.trim()) {
            setError('이메일을 입력해주세요.');
            setIsLoading(false);
            return {
                success: false,
                message: '이메일을 입력해주세요.',
            };
        }

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(email)) {
            setError('올바른 이메일 형식이 아닙니다.');
            setIsLoading(false);
            return {
                success: false,
                message: '올바른 이메일 형식이 아닙니다.',
            };
        }

        try {
            const response = await findAccount({
                type: 'id',
                name: name.trim(),
                email: email.trim(),
            });

            if (response.success) {
                return {
                    success: true,
                    userId: response.userId,
                    message: response.message || '아이디 찾기가 완료되었습니다.',
                };
            } else {
                setError(response.message || '아이디 찾기에 실패했습니다.');
                return {
                    success: false,
                    message: response.message || '아이디 찾기에 실패했습니다.',
                };
            }
        } catch (err: any) {
            const errorMessage = err.message || '아이디 찾기 중 오류가 발생했습니다.';
            setError(errorMessage);
            return {
                success: false,
                message: errorMessage,
            };
        } finally {
            setIsLoading(false);
        }
    }, [navigate]);

    const handleFindPassword = useCallback(async (userId: string, email: string): Promise<FindAccountResult> => {
        setIsLoading(true);
        setError(null);

        // 유효성 검사
        if (!userId || !userId.trim()) {
            setError('아이디를 입력해주세요.');
            setIsLoading(false);
            return {
                success: false,
                message: '아이디를 입력해주세요.',
            };
        }

        if (!email || !email.trim()) {
            setError('이메일을 입력해주세요.');
            setIsLoading(false);
            return {
                success: false,
                message: '이메일을 입력해주세요.',
            };
        }

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(email)) {
            setError('올바른 이메일 형식이 아닙니다.');
            setIsLoading(false);
            return {
                success: false,
                message: '올바른 이메일 형식이 아닙니다.',
            };
        }

        try {
            const response = await findAccount({
                type: 'password',
                userId: userId.trim(),
                email: email.trim(),
            });

            if (response.success) {
                // exists가 true이면 비밀번호 변경 페이지로 이동
                if (response.exists === true) {
                    // userId를 localStorage에 저장 (비밀번호 변경 페이지에서 사용)
                    if (userId) {
                        localStorage.setItem('userId', userId.trim());
                    }
                    // 비밀번호 찾기에서 온 경우임을 표시하기 위한 쿼리 파라미터 추가
                    navigate('/password-setup?from=find-password');
                    return {
                        success: true,
                        exists: true,
                        message: '비밀번호 변경 페이지로 이동합니다.',
                    };
                }
                // exists가 false이면 에러 메시지로 표시
                const errorMessage = response.message || '일치하는 계정을 찾을 수 없습니다.';
                setError(errorMessage);
                return {
                    success: false,
                    exists: false,
                    message: errorMessage,
                };
            } else {
                setError(response.message || '비밀번호 찾기에 실패했습니다.');
                return {
                    success: false,
                    message: response.message || '비밀번호 찾기에 실패했습니다.',
                };
            }
        } catch (err: any) {
            const errorMessage = err.message || '비밀번호 찾기 중 오류가 발생했습니다.';
            setError(errorMessage);
            return {
                success: false,
                message: errorMessage,
            };
        } finally {
            setIsLoading(false);
        }
    }, [navigate]);

    return {
        handleFindId,
        handleFindPassword,
        isLoading,
        error,
    };
};

